// Copyright ©️ 2026 eVolve MEP, LLC

import { Combobox, ComboboxClearButton, Group, type InputProps, Loader, type Primitive } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import { CreatedBadge } from 'components/Mantine/TypeSafeSelect/Creatables/CreatedBadge';
import type { WrappedMultiSelectProps, WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';

export const useSelectRightSection = <T extends Primitive, D extends object = object>(
  {
    dropdownOpened,
    hasValue,
    onClear,
  }: {
    dropdownOpened: boolean;
    hasValue: boolean;
    onClear: () => void;
  },
  {
    loading,
    alwaysShowWhenLoading,
    disabled,
    size = 'sm',
    rightSection,
    rightSectionProps,
    clearable,
    clearButtonProps,
    creatable,
    createdValue,
  }: Pick<
    WrappedSelectProps<T, D> & WrappedMultiSelectProps<T, D>,
    | 'loading'
    | 'alwaysShowWhenLoading'
    | 'disabled'
    | 'size'
    | 'rightSection'
    | 'rightSectionProps'
    | 'clearable'
    | 'clearButtonProps'
    | 'creatable'
    | 'createdValue'
  >,
): Pick<InputProps, 'rightSection' | 'rightSectionPointerEvents' | 'rightSectionProps' | 'rightSectionWidth'> => {
  const { ref, width } = useElementSize();

  if (loading && (dropdownOpened || hasValue || alwaysShowWhenLoading)) {
    return {
      rightSection: <Loader size={size} />,
    };
  }

  const showCreatedBadge = creatable && !!createdValue && !dropdownOpened;

  return {
    rightSection: (
      <Group gap={4} wrap="nowrap" flex="none" ref={ref}>
        {showCreatedBadge && <CreatedBadge />}
        {rightSection ??
          (clearable && hasValue ? (
            <ComboboxClearButton
              onClear={onClear}
              // to prevent the dropdown from opening/closing on click
              onMouseDown={(e) => e.stopPropagation()}
              disabled={disabled}
              size={size}
              {...clearButtonProps}
            />
          ) : (
            <Combobox.Chevron style={{ cursor: 'pointer' }} />
          ))}
      </Group>
    ),
    rightSectionWidth: showCreatedBadge ? width : undefined,
    rightSectionProps,
    rightSectionPointerEvents: !rightSection && !dropdownOpened && (!clearable || !hasValue) ? 'none' : 'all',
  };
};
